import React, {useState} from 'react'
import {TComponentStreamPlayer} from './_types'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import ComponentInput from '../ComponentInput'
import {Formik, useField, useFormikContext} from 'formik'
import {useIntl} from 'react-intl'
import {Col, Form, Modal} from 'react-bootstrap'
import ComponentButton from '../ComponentButton'
import ComponentTextarea from '../ComponentTextarea'
import ComponentTooltip from '../ComponentTooltip'
import ComponentSelect from '../ComponentSelect'

type StreamProps = {
    type: string
    value: string
}

type ModalProps = {
    show: boolean
    handleClose: (props: StreamProps | undefined) => void
    values: StreamProps
}

type TVideoJsOptions = {
    autoplay: string
    controls: boolean
    fluid: boolean
}

type TRtspPlayer = {
    url: string
    // type?: 'vk' | 'rtsp' | string
    // value?: string
}

type TIFrame = {
    code: string
}

const options: TVideoJsOptions = {
    autoplay: 'muted',
    controls: false,
    fluid: true,
}

const fieldTypesList = [
    {title: 'VK', value: 'vk'},
    {title: 'RTSP-поток', value: 'rtsp'},
]
const CODE_TITLE = 'Код'
const URL_TITLE = 'URL потока'
const TYPE_TITLE = 'Тип потока'
const VIDEO_STREAM_EDIT_TITLE = 'Редактирование видеопотока'

const ComponentStreamPlayer: React.FC<TComponentStreamPlayer> = (props) => {
    const {article, is_edit_stream_player = true} = props
    const intl = useIntl()
    const [{value}] = useField(article)
    const {setFieldValue} = useFormikContext<any>()
    const [modal, showModal] = useState<boolean>(false)
    const values = value ? JSON.parse(value) : {type: '', value: ''}
    const modalButtonClick = () => {
        showModal(true)
    }
    return (
        <>
            {values?.type ? (
                <div className='player-container'>
                    {values?.type == 'rtsp' && <RtspPlayer url={values?.value || ''}></RtspPlayer>}
                    {values?.type == 'vk' && <VkIframe code={values?.value || ''}></VkIframe>}
                    {is_edit_stream_player && (
                        <ComponentButton
                            className='edit-button'
                            type='custom'
                            defaultLabel='icon'
                            settings={{
                                title: 'Редактировать',
                                background: 'dark',
                                icon: 'edit',
                            }}
                            customHandler={modalButtonClick}
                        />
                    )}
                </div>
            ) : (
                <ComponentButton
                    type='custom'
                    settings={{
                        title: intl.formatMessage({id: 'BUTTON.APPEND'}),
                        background: 'dark',
                        icon: '',
                    }}
                    customHandler={() => showModal(true)}
                />
            )}
            <EditModal
                show={modal}
                values={values}
                handleClose={(modalProps) => {
                    showModal(false)
                    if (modalProps) {
                        setFieldValue(props.article, JSON.stringify(modalProps))
                    }
                }}
            ></EditModal>
        </>
    )
}

ComponentStreamPlayer.defaultProps = {
    article: '',
}

export const RtspPlayer: React.FC<TRtspPlayer> = ({url}) => {
    const videoRef = React.useRef<any>(null)
    const playerRef = React.useRef<any>(null)

    React.useEffect(() => {
        if (!playerRef.current) {
            const videoElement = document.createElement('video-js')

            videoElement.classList.add('vjs-big-play-centered')
            videoRef.current.appendChild(videoElement)
            const player: any = (playerRef.current = videojs(videoElement, options, () => {
                player.src(url)
                player.autoplay(options.autoplay)
            }))
        } else {
            const player = playerRef.current

            player.src(url)
            player.autoplay(options.autoplay)
        }
    }, [url, options, videoRef])

    React.useEffect(() => {
        const player = playerRef.current

        return () => {
            if (player && !player.isDisposed()) {
                player.dispose()
                playerRef.current = null
            }
        }
    }, [playerRef])
    return (
        <div data-vjs-player>
            <div ref={videoRef} />
        </div>
    )
}

export const VkIframe: React.FC<TIFrame> = ({code}) => {
    return (
        <div className='vk-container'>
            <div dangerouslySetInnerHTML={{__html: code}}></div>
        </div>
    )
}

const EditModal: React.FC<ModalProps> = ({show, handleClose, values}) => {
    const intl = useIntl()
    return (
        <Modal show={show}>
            <Formik initialValues={values} onSubmit={handleClose}>
                {({values, handleSubmit}) => (
                    <Form>
                        <Modal.Header>
                            <Modal.Title>{VIDEO_STREAM_EDIT_TITLE}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className='scroll-y'>
                            <div className='moduleForm_field col-md-12'>
                                <div className='moduleForm_field_labelContainer'>
                                    <ComponentTooltip title={TYPE_TITLE}>
                                        <label className='moduleForm_field_label required'>
                                            {TYPE_TITLE}
                                        </label>
                                    </ComponentTooltip>
                                </div>
                                <div className='moduleForm_field_container col-md-12'>
                                    <Form.Group as={Col} md={12}>
                                        <ComponentSelect
                                            article='type'
                                            data_type='string'
                                            list={fieldTypesList}
                                        />
                                    </Form.Group>
                                </div>
                                <span className='moduleForm_field_description'></span>
                            </div>
                            {values?.type === 'vk' && (
                                <div className='moduleForm_field col-md-12'>
                                    <div className='moduleForm_field_labelContainer'>
                                        <ComponentTooltip title={CODE_TITLE}>
                                            <label className='moduleForm_field_label required'>
                                                {CODE_TITLE}
                                            </label>
                                        </ComponentTooltip>
                                    </div>
                                    <div className='moduleForm_field_container col-md-12'>
                                        <ComponentTextarea article='value' />
                                    </div>
                                    <span className='moduleForm_field_description'></span>
                                </div>
                            )}
                            {values?.type === 'rtsp' && (
                                <div className='moduleForm_field col-md-12'>
                                    <div className='moduleForm_field_labelContainer'>
                                        <ComponentTooltip title={URL_TITLE}>
                                            <label className='moduleForm_field_label required'>
                                                {URL_TITLE}
                                            </label>
                                        </ComponentTooltip>
                                    </div>
                                    <div className='moduleForm_field_container col-md-12'>
                                        <ComponentInput article='value' field_type='string' />
                                    </div>
                                    <span className='moduleForm_field_description'></span>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <ComponentButton
                                type='custom'
                                settings={{
                                    title: intl.formatMessage({id: 'BUTTON.CANCEL'}),
                                    background: 'danger',
                                    icon: '',
                                }}
                                customHandler={() => handleClose(undefined)}
                            />
                            <ComponentButton
                                type='custom'
                                settings={{
                                    title: intl.formatMessage({id: 'BUTTON.SAVE'}),
                                    background: 'dark',
                                    icon: '',
                                }}
                                customHandler={handleSubmit}
                            />
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    )
}

export default ComponentStreamPlayer
